/**
 * The style of a business's menus (docs/08-entscheidungen.md, S15): for each part of a menu – the
 * name of the house, a section, a dish, its price … – a font, a size, a colour, bold, italic, how it
 * is written and how far apart its letters stand; the space between sections and dishes, the
 * widths of the columns of number and price, the paper and its margins. A business keeps several
 * styles under names; each menu has one (the house style unless chosen). For print, PDF, Word and,
 * when wanted, the menu on the net. „Klassisch“ is the look the app always had.
 */

import type { Lang } from "@household-kit/lang";
import { logoPlaceOf } from "./logo.ts";
import type { LogoPlace } from "./logo.ts";

/**
 * The fonts to choose from: the two standard fonts of PDF and seven free ones (SIL OFL) the app carries;
 * after them two ornamental ones for headings only (S56), with the cuts they have.
 */
export const DESIGN_FONTS = [
  { id: "times", label: "Times", kind: "serif", css: '"Times New Roman", Times, serif', word: "Times New Roman", file: null },
  { id: "helvetica", label: "Helvetica", kind: "sans", css: "Helvetica, Arial, sans-serif", word: "Arial", file: null },
  { id: "garamond", label: "EB Garamond", kind: "serif", css: '"EB Garamond", Garamond, serif', word: "EB Garamond", file: "eb-garamond" },
  { id: "lora", label: "Lora", kind: "serif", css: "Lora, Georgia, serif", word: "Lora", file: "lora" },
  { id: "playfair", label: "Playfair Display", kind: "serif", css: '"Playfair Display", Georgia, serif', word: "Playfair Display", file: "playfair-display" },
  { id: "baskerville", label: "Libre Baskerville", kind: "serif", css: '"Libre Baskerville", Baskerville, serif', word: "Libre Baskerville", file: "libre-baskerville" },
  { id: "sourcesans", label: "Source Sans 3", kind: "sans", css: '"Source Sans 3", "Helvetica Neue", Arial, sans-serif', word: "Source Sans 3", file: "source-sans-3" },
  { id: "montserrat", label: "Montserrat", kind: "sans", css: 'Montserrat, "Helvetica Neue", Arial, sans-serif', word: "Montserrat", file: "montserrat" },
  { id: "josefin", label: "Josefin Sans", kind: "sans", css: '"Josefin Sans", "Helvetica Neue", Arial, sans-serif', word: "Josefin Sans", file: "josefin-sans" },
  { id: "grenze", label: "Grenze Gotisch", kind: "display", css: '"Grenze Gotisch", Georgia, serif', word: "Grenze Gotisch", file: "grenze-gotisch", cuts: { bold: true, italic: false } },
  { id: "uncial", label: "Uncial Antiqua", kind: "display", css: '"Uncial Antiqua", Georgia, serif', word: "Uncial Antiqua", file: "uncial-antiqua", cuts: { bold: false, italic: false } },
] as const;
export type FontId = (typeof DESIGN_FONTS)[number]["id"];
export const fontOf = (id: FontId) => DESIGN_FONTS.find((f) => f.id === id) ?? DESIGN_FONTS[0];
/** Which of bold and italic a font has; the ornamental ones (S56) lack some, the others have both. */
export const fontCuts = (id: FontId): { bold: boolean; italic: boolean } => {
  const f = fontOf(id);
  return "cuts" in f ? f.cuts : { bold: true, italic: true };
};
/** An ornamental font (S56): only for headings, never in capitals. */
export const isDisplayFont = (id: FontId) => fontOf(id).kind === "display";

/**
 * Real small capitals (T9, docs/13-typografie.md): fonts of their own whose small letters are drawn
 * as small capitals – EB Garamond SC (Georg Duffner) and Playfair Display SC, upright and regular.
 * A style in small capitals of EB Garamond or Playfair Display takes them; bold or italic small
 * capitals and the other fonts are still made of capitals at the height of the small letters.
 */
export const SMALL_CAPS_FONTS: Partial<Record<FontId, { file: string; label: string; word: string }>> = {
  garamond: { file: "eb-garamond-sc-400-normal.woff", label: "EB Garamond SC", word: "EB Garamond SC 12" },
  playfair: { file: "playfair-display-sc-400-normal.woff", label: "Playfair Display SC", word: "Playfair Display SC" },
};
/** The font of real small capitals for a style, or null. */
export const smallCapsFont = (s: TextStyle) => (s.caps === "small" && !s.bold && !s.italic ? (SMALL_CAPS_FONTS[s.font] ?? null) : null);

/** A style's fonts as CSS names them: the font of real small capitals first, where it has one. */
export const cssFamily = (s: TextStyle) => {
  const sc = smallCapsFont(s);
  return sc ? `"${sc.label}", ${fontOf(s.font).css}` : fontOf(s.font).css;
};
/** CSS's small-caps only where no font of real small capitals draws them (it draws them from the small letters). */
export const cssVariant = (s: TextStyle) => (s.caps === "small" && !smallCapsFont(s) ? "small-caps" : "normal");

/** A file of designFiles as @font-face takes it: the family, the weight and the slant. */
export function fontFaceOf(file: string): { family: string; weight: string; style: string } {
  const [, name, weight, style] = /^(.*)-(400|700)-(normal|italic)\.woff$/.exec(file)!;
  const sc = Object.values(SMALL_CAPS_FONTS).find((f) => f.file === file);
  return { family: sc?.label ?? DESIGN_FONTS.find((f) => f.file === name)!.label, weight: weight!, style: style! };
}

/** The file of a free font in the app (public/fonts): bold or not, italic or not. */
export function fontFile(id: FontId, bold: boolean, italic: boolean): string | null {
  const f = fontOf(id);
  if (!f.file) return null;
  // A cut the font lacks (S56) is never made up: its regular one.
  const cuts = fontCuts(id);
  return `${f.file}-${bold && cuts.bold ? 700 : 400}-${italic && cuts.italic ? "italic" : "normal"}.woff`;
}

/**
 * Free fonts as wide as Times and Helvetica (Arial), letter for letter (docs/21-tuerkisch.md § 3.1):
 * the PDF writes Times and Helvetica only in WinAnsi, so a card with a letter beyond it („Kır“, „şiş“,
 * „Łódź“) is set in these instead, for the whole style; lines and pages fall as before.
 */
export const STAND_IN = { times: "tinos", helvetica: "arimo" } as const;
export const standInFile = (base: keyof typeof STAND_IN, bold: boolean, italic: boolean) => `${STAND_IN[base]}-${bold ? 700 : 400}-${italic ? "italic" : "normal"}.woff`;

/** Colours to tap, matching the menus; any other can be typed in. */
export const DESIGN_COLORS = [
  { hex: "#2a2522", label: "Tinte" },
  { hex: "#000000", label: "Schwarz" },
  { hex: "#5c524b", label: "Dunkelgrau" },
  { hex: "#756a62", label: "Warmgrau" },
  { hex: "#a2461a", label: "Terrakotta" },
  { hex: "#7a1f2b", label: "Bordeaux" },
  { hex: "#806320", label: "Gold" },
  { hex: "#5b6b2f", label: "Olive" },
  { hex: "#2f6b6b", label: "Petrol" },
  { hex: "#1f3a5f", label: "Marine" },
] as const;

export type Caps = "normal" | "upper" | "small";
export interface TextStyle {
  font: FontId;
  /** Points. */
  size: number;
  /** „#a2461a“. */
  color: string;
  bold: boolean;
  italic: boolean;
  caps: Caps;
  /** Space between the letters, in hundredths of the size (0: none). */
  tracking: number;
}

/** The parts of a menu that have a style of their own, in the order of the page. */
export const STYLE_GROUPS = [
  { label: "Kopf", keys: ["house", "title", "lead"] },
  // „Unterabschnitt“ (S44) only with a subsection on the menu; it looks like a course of a set menu at first.
  { label: "Abschnitte", keys: ["section", "subsection", "sectionNote"] },
  // „Mengen über den Preisen“ (S46) only where the menu has such a head: over the prices of a section („30 cm“) or of drinks.
  { label: "Speisen", keys: ["number", "dish", "description", "variant", "price", "sizeHead", "codes", "tags", "itemNote"] },
  { label: "Textbausteine", keys: ["textTitle", "text", "textLine"] },
  // Only with a set menu on the menu (S32): a course's title, „oder“ between the dishes of a course.
  { label: "Menüs", keys: ["course", "orLine"] },
  // Only with drinks on the menu (S22): the heading „Getränke“ between the parts.
  { label: "Getränke", keys: ["part"] },
  { label: "Fuß", keys: ["menuNote", "legend"] },
] as const;
export type StyleKey = (typeof STYLE_GROUPS)[number]["keys"][number];
/**
 * Every style, in the order a style is stored and compared in (designOf, the digest of the net): as it
 * was, whatever group shows it now. „Mengen über den Preisen“ moved to the dishes (S46), not here.
 */
export const STYLE_KEYS: readonly StyleKey[] = ["house", "title", "lead", "section", "subsection", "sectionNote", "number", "dish", "description", "variant", "price", "codes", "tags", "itemNote", "textTitle", "text", "textLine", "course", "orLine", "part", "sizeHead", "menuNote", "legend"];
/** The headings an ornamental font (S56) may set: never dishes, descriptions, prices or texts. */
export const DISPLAY_KEYS: readonly StyleKey[] = ["house", "title", "section", "subsection", "course", "part", "textTitle"];
/**
 * A style as an ornamental font allows it (S56): on a part that is no heading the font falls back;
 * else no capitals and no small capitals, bold and italic only where the font has them.
 */
export function fitDisplay(key: StyleKey | null, s: TextStyle, fallback: FontId): TextStyle {
  if (!isDisplayFont(s.font)) return s;
  if (!key || !DISPLAY_KEYS.includes(key)) return { ...s, font: isDisplayFont(fallback) ? "garamond" : fallback };
  const cuts = fontCuts(s.font);
  return { ...s, caps: "normal", bold: s.bold && cuts.bold, italic: s.italic && cuts.italic };
}
export const STYLE_LABELS: Record<StyleKey, string> = {
  house: "Name des Hauses",
  title: "Titel der Speisekarte",
  lead: "Untertitel",
  section: "Abschnitt",
  subsection: "Unterabschnitt",
  sectionNote: "Hinweis zum Abschnitt",
  number: "Nummer",
  dish: "Speise",
  description: "Beschreibung",
  variant: "Preiszeile",
  price: "Preis",
  codes: "Allergene",
  tags: "Merkmale",
  itemNote: "Hinweis zur Speise",
  textTitle: "Textbaustein-Überschrift",
  text: "Textbaustein-Beschreibung",
  textLine: "Textbaustein einzeilig",
  course: "Gang im Menü",
  orLine: "„oder“ und Trenner im Menü",
  part: "Überschrift „Getränke“",
  sizeHead: "Mengen über den Preisen",
  menuNote: "Hinweis der Speisekarte",
  legend: "Legende und Stand",
};

/** Paper sizes in points; DIN lang is the tall narrow card (99 × 210 mm). */
export const PAGE_FORMATS = [
  { id: "a4", label: "A4", width: 595.28, height: 841.89 },
  { id: "a5", label: "A5", width: 419.53, height: 595.28 },
  { id: "dinlang", label: "DIN lang", width: 280.63, height: 595.28 },
] as const;
export type PageFormat = (typeof PAGE_FORMATS)[number]["id"];
export const pageOf = (id: PageFormat) => PAGE_FORMATS.find((f) => f.id === id) ?? PAGE_FORMATS[0];
/** Narrower than this (in points), a column of dishes is too narrow: „Zweispaltig“ becomes one column. */
export const MIN_COLUMN = 170;
/** Millimetres to points. */
export const mm = (v: number) => (v * 72) / 25.4;

/** The parts of a dish that drinks may have in a look of their own (S22). */
// „Mengen über den Preisen“ last (S46): the order of a stored style stays as it was.
export const DRINK_STYLE_KEYS = ["number", "dish", "description", "variant", "price", "codes", "tags", "itemNote", "sizeHead"] as const satisfies readonly StyleKey[];
export type DrinkStyleKey = (typeof DRINK_STYLE_KEYS)[number];
/** The parts of a drink in a set menu that may have a look of their own (S53): no number, no amounts over prices there. */
export const SET_DRINK_STYLE_KEYS = ["dish", "description", "variant", "price", "codes", "tags", "itemNote"] as const satisfies readonly DrinkStyleKey[];
export type SetDrinkStyleKey = (typeof SET_DRINK_STYLE_KEYS)[number];

export interface Design {
  styles: Record<StyleKey, TextStyle>;
  /**
   * „Unterschiedliche Formatierung für Speisen und Getränke“ (S22): the drinks' own look of a dish's
   * parts; without it (coupled) the drinks look as the food.
   */
  drinkStyles?: Record<DrinkStyleKey, TextStyle>;
  /**
   * „Unterschiedliche Formatierung für Getränke im Menü“ (S53): the look of a drink in a set menu,
   * below a course; without it (coupled) it looks as the drinks, which without theirs look as the food.
   */
  setDrinkStyles?: Record<SetDrinkStyleKey, TextStyle>;
  page: {
    format: PageFormat;
    /** Margin left and right, mm. */
    marginX: number;
    /** Margin at the top, mm; the bottom has 4 mm more, for the page number. */
    marginY: number;
    /** The top margin of a menu set at its scale (T14, scaleDesign), mm; never stored with a style. */
    top?: number;
  };
  /** The columns of a dish: number and price in points; the dish has the rest. */
  columns: { number: number; price: number };
  /** How far the lines of further prices (S19) begin right of the dish's name, in points (up to 120 mm). */
  variantIndent: number;
  /** The width of the head's text (name, title, subtitle), in percent of what it may have. */
  headWidth: number;
  /** „3,80 €“ instead of „3,80“. */
  euro: boolean;
  /** The same for the drinks, only with their own look (drinkStyles); without it they follow `euro`. */
  drinkEuro?: boolean;
  /** The menu on the net in this style too (fonts, colours, bold …; its sizes for the phone). */
  web: boolean;
  /** Where the business's logo stands (S13): above the head, or at its side. */
  logoPlace: LogoPlace;
  /** „Logo statt Name“: the logo alone at the top (only with a logo). */
  logoOnly: boolean;
  /** „Logo anzeigen: aus“ – the menus of this style without the logo. */
  logoHidden: boolean;
  /** The logo's box in print, PDF and Word in percent of its usual size (S13); none: 100. Not on the net. */
  logoSize?: number;
  /** The logo once more in the foot of each page without it in the head (S43), here; none: not. Print, PDF and Word. */
  footLogo?: FootLogoPlace;
  /** That logo's box in percent of its usual size (FOOT_LOGO_SIZE); none: 100. */
  footLogoSize?: number;
  /**
   * „Satz der Speisen: mittig“ (S48): sections, dishes, prices and texts each line in the middle, the
   * prices below the dish (Kompakt: after its name), menus as „Menü mittig“. None: the price column at
   * the right. Print, PDF and Word; the net keeps its own (T13).
   */
  align?: "center";
  /** The head when the logo is not at its side. */
  headAlign: "center" | "left";
  /** A line under each section's title. */
  rule: boolean;
  /** Points above a section. */
  sectionSpace: number;
  /** Points between two dishes. */
  dishSpace: number;
  /** The lines of a dish (name, description, further prices, note): closer, as they were, or further apart. */
  lineSpacing: LineSpacing;
  /** The word between the dishes of a course of a set menu (S32): „oder“, „o“, „– oder –“. */
  orText: string;
  /** „Darstellung der Menüs“ (S32, 12-wahlmenue.md § 12): courses indented, as a column, or the menu centred. */
  setLayout: SetLayout;
  /** The word after the title of a course to choose from: „zur Wahl“. */
  choiceText: string;
  /** „Zwischen den Gängen“ (S57): a mark between two courses of a set menu; none: only the space. */
  courseMark?: CourseMark;
  /** „Übersetzung“ on a bilingual card (S60): a rule against the German above it; missing: italics the other way round. */
  translation?: TranslationRule;
  /** The template it began with; empty once changed. */
  preset: PresetId | "";
}

export const SIZE_LIMITS = { min: 5, max: 40 };
export const MARGIN_X = { min: 5, max: 40 };
export const MARGIN_Y = { min: 5, max: 40 };
export const NUMBER_COLUMN = { min: 0, max: 60 };
export const PRICE_COLUMN = { min: 24, max: 120 };
export const SECTION_SPACE = { min: 0, max: 48 };
/** At least 2 pt: with less, two dishes stand as close as the lines of one and run together. */
export const DISH_SPACE = { min: 2, max: 20 };
/** Up to 120 mm: the PDF keeps at least a little of the dish's column for the label. */
export const VARIANT_INDENT = { min: 0, max: 340 };
export const HEAD_WIDTH = { min: 30, max: 100 };
/** The logo's box in percent (S13): up to three times as large, for a tall logo or a cover. */
export const LOGO_SIZE = { min: 50, max: 300, fallback: 100 };
/** Where the logo stands in the foot of the pages (S43). */
export type FootLogoPlace = "left" | "center" | "right";
export const FOOT_LOGO_PLACES: readonly FootLogoPlace[] = ["left", "center", "right"];
/** The foot's logo in percent of its box (about 40 × 14 mm). */
export const FOOT_LOGO_SIZE = { min: 50, max: 300, fallback: 100 };
/** „oder“ unless the style says another word (S32). */
export const OR_TEXT = { fallback: "oder", max: 24 };
/** „zur Wahl“ unless the style says another word (S32 § 12). */
export const CHOICE_TEXT = { fallback: "zur Wahl", max: 24 };
export type SetLayout = "indent" | "columns" | "center";
export const SET_LAYOUTS: { id: SetLayout; label: string; hint: string }[] = [
  { id: "indent", label: "Gänge eingerückt", hint: "Gangtitel eingerückt, die Speisen darunter noch eine Stufe" },
  { id: "columns", label: "Gänge als Spalte", hint: "Gangtitel links neben den Speisen; auf schmalem Papier eingerückt" },
  { id: "center", label: "Menü mittig", hint: "alles mittig, „— oder —“ dazwischen" },
];
/** The marks between the courses of a set menu (S57), set in the look of „oder“. */
export type CourseMark = "line" | "stars" | "fleuron";
/**
 * The translation on a bilingual card (S36, S60), a rule against the German text above it rather than
 * a style of its own: it follows whatever that text looks like – the food's description, the drinks'
 * own (S22), a drink's in a set menu (S53), a text of the catalogue (S14) –, coupled or not, without a
 * coupling of its own. Italic the other way round, or as the German; the German's colour or its own;
 * as large or up to 3 points smaller.
 */
export interface TranslationRule {
  italic: "flip" | "same";
  /** „#3f6f8f“; null: the German's. */
  color: string | null;
  /** Points smaller than the German. */
  smaller: number;
}
export const TRANSLATION_SMALLER = { min: 0, max: 3, step: 0.5 } as const;
export const DEFAULT_TRANSLATION: TranslationRule = { italic: "flip", color: null, smaller: 0 };
export const translationOf = (d: Pick<Design, "translation">): TranslationRule => d.translation ?? DEFAULT_TRANSLATION;
/** The look of a translation below a text in `base`'s look, by the style's rule. */
export function translationStyle(base: TextStyle, d: Pick<Design, "translation">): TextStyle {
  const r = translationOf(d);
  return { ...base, italic: r.italic === "flip" ? !base.italic : base.italic, ...(r.color ? { color: r.color } : {}), size: Math.max(5, Math.round((base.size - r.smaller) * 10) / 10) };
}
/** The rule from what is stored; null when it is the usual one (not stored then). */
function translationRuleOf(v: unknown): TranslationRule | null {
  const o = v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const color = typeof o.color === "string" && HEX.test(o.color.toLowerCase()) ? o.color.toLowerCase() : null;
  const smaller = Math.round(num(o.smaller, TRANSLATION_SMALLER.min, TRANSLATION_SMALLER.max, 0) / TRANSLATION_SMALLER.step) * TRANSLATION_SMALLER.step;
  const rule: TranslationRule = { italic: o.italic === "same" ? "same" : "flip", color, smaller };
  return rule.italic === "flip" && !rule.color && !rule.smaller ? null : rule;
}

export const COURSE_MARKS: { id: CourseMark; label: string; hint: string }[] = [
  { id: "line", label: "feine Linie", hint: "kurz und haarfein, mittig" },
  { id: "stars", label: "Sternchen", hint: "* * * gesperrt und gesenkt" },
  { id: "fleuron", label: "Zierzeichen", hint: "❦" },
];
/** What a mark between courses writes; the line is drawn. */
export const COURSE_MARK_TEXT: Record<Exclude<CourseMark, "line">, string> = { stars: "*\u2002*\u2002*", fleuron: "❦" };
export type LineSpacing = "tight" | "normal" | "wide";
/** „Zeilenabstand“ of the dishes: how far apart their lines stand, as a share of what they always had (1.24 × the size). */
export const LINE_SPACINGS = [
  { id: "tight", label: "eng", factor: 1.15 / 1.24 },
  { id: "normal", label: "normal", factor: 1 },
  { id: "wide", label: "weit", factor: 1.35 / 1.24 },
] as const satisfies readonly { id: LineSpacing; label: string; factor: number }[];
export const lineSpacingOf = (d: Design) => LINE_SPACINGS.find((l) => l.id === d.lineSpacing) ?? LINE_SPACINGS[1];

export const TRACKINGS = [
  { value: 0, label: "keine" },
  { value: 5, label: "leicht" },
  { value: 12, label: "mittel" },
  { value: 22, label: "weit" },
] as const;

const INK = "#2a2522";
// Warm grey and gold at least 5 : 1 on white: the smallest texts are in them (T4).
const MUTED = "#756a62";
const SOFT = "#5c524b";
const ACCENT = "#a2461a";

const st = (font: FontId, size: number, color: string, more: Partial<TextStyle> = {}): TextStyle => ({ font, size, color, bold: false, italic: false, caps: "normal", tracking: 0, ...more });

/**
 * The look the app always had; since T10 (docs/13-typografie.md) dishes, prices, descriptions and
 * the small texts about a point larger than before, as menus are read in dim light.
 */
const CLASSIC: Design = {
  styles: {
    // Name, sections, courses and „Getränke“ in real small capitals of EB Garamond (T9).
    house: st("garamond", 26, INK, { caps: "small", tracking: 6 }),
    title: st("helvetica", 8.5, MUTED, { caps: "upper", tracking: 21 }),
    lead: st("times", 10.5, MUTED, { italic: true }),
    section: st("garamond", 16, ACCENT, { caps: "small", tracking: 4 }),
    subsection: st("garamond", 13, ACCENT, { caps: "small", tracking: 4 }),
    sectionNote: st("times", 9.5, MUTED, { italic: true }),
    number: st("times", 10, ACCENT),
    dish: st("times", 11.5, INK, { bold: true }),
    description: st("times", 10, SOFT, { italic: true }),
    variant: st("times", 10, SOFT, { italic: true }),
    price: st("times", 11.5, INK),
    codes: st("helvetica", 7, MUTED),
    tags: st("helvetica", 7, ACCENT),
    itemNote: st("helvetica", 8, ACCENT),
    textTitle: st("times", 12.5, ACCENT, { italic: true }),
    text: st("times", 11, INK),
    textLine: st("times", 11, INK, { bold: true }),
    course: st("garamond", 13, ACCENT, { caps: "small", tracking: 4 }),
    orLine: st("times", 10, SOFT, { italic: true }),
    part: st("garamond", 22.4, ACCENT, { caps: "small", tracking: 4 }),
    sizeHead: st("helvetica", 6.5, MUTED, { caps: "upper", tracking: 10 }),
    menuNote: st("times", 10, SOFT, { italic: true }),
    legend: st("helvetica", 8, MUTED),
  },
  headAlign: "center",
  rule: true,
  sectionSpace: 14,
  dishSpace: 7.2,
  lineSpacing: "normal",
  orText: OR_TEXT.fallback,
  setLayout: "indent",
  choiceText: CHOICE_TEXT.fallback,
  preset: "classic",
  page: { format: "a4", marginX: 16, marginY: 16 },
  columns: { number: 20, price: 40 },
  variantIndent: 0,
  headWidth: 100,
  euro: false,
  web: true,
  logoPlace: "center",
  logoOnly: false,
  logoHidden: false,
};

const NAVY = "#1f3a5f";
const MODERN: Design = {
  styles: {
    house: st("montserrat", 22, INK, { bold: true, caps: "upper", tracking: 8 }),
    title: st("sourcesans", 9, MUTED, { caps: "upper", tracking: 22 }),
    lead: st("sourcesans", 10.5, MUTED),
    section: st("montserrat", 12, NAVY, { bold: true, caps: "upper", tracking: 12 }),
    subsection: st("montserrat", 10, NAVY, { bold: true, caps: "upper", tracking: 12 }),
    sectionNote: st("sourcesans", 9.5, MUTED, { italic: true }),
    number: st("sourcesans", 9.5, NAVY, { bold: true }),
    dish: st("sourcesans", 11.5, INK, { bold: true }),
    description: st("sourcesans", 10, SOFT),
    variant: st("sourcesans", 10, SOFT),
    price: st("sourcesans", 11.5, INK, { bold: true }),
    codes: st("sourcesans", 7, MUTED),
    tags: st("sourcesans", 7, NAVY),
    itemNote: st("sourcesans", 8, NAVY),
    textTitle: st("montserrat", 11, NAVY, { bold: true }),
    text: st("sourcesans", 11, INK),
    textLine: st("sourcesans", 11, INK, { bold: true }),
    course: st("montserrat", 10, NAVY, { bold: true, caps: "upper", tracking: 12 }),
    orLine: st("sourcesans", 10, SOFT, { italic: true }),
    part: st("montserrat", 16, NAVY, { bold: true, caps: "upper", tracking: 12 }),
    sizeHead: st("sourcesans", 7, MUTED, { caps: "upper", tracking: 10 }),
    menuNote: st("sourcesans", 10, SOFT, { italic: true }),
    legend: st("sourcesans", 8, MUTED),
  },
  headAlign: "left",
  rule: false,
  sectionSpace: 18,
  dishSpace: 8,
  lineSpacing: "normal",
  orText: OR_TEXT.fallback,
  setLayout: "indent",
  choiceText: CHOICE_TEXT.fallback,
  preset: "modern",
  page: { format: "a4", marginX: 20, marginY: 18 },
  columns: { number: 22, price: 48 },
  variantIndent: 0,
  headWidth: 100,
  euro: true,
  web: true,
  logoPlace: "center",
  logoOnly: false,
  logoHidden: false,
};

const BORDEAUX = "#7a1f2b";
const GOLD = "#806320";
const ELEGANT: Design = {
  styles: {
    // Real small capitals of Playfair Display (T9), wider than made ones: a little smaller.
    house: st("playfair", 24, INK, { caps: "small", tracking: 5 }),
    title: st("lora", 8.5, GOLD, { caps: "upper", tracking: 22 }),
    lead: st("lora", 10.5, MUTED, { italic: true }),
    section: st("playfair", 16, BORDEAUX, { italic: true }),
    subsection: st("playfair", 12.5, BORDEAUX, { italic: true }),
    sectionNote: st("lora", 9.5, MUTED, { italic: true }),
    number: st("lora", 9.5, GOLD),
    dish: st("playfair", 12, INK),
    description: st("lora", 9.8, SOFT, { italic: true }),
    variant: st("lora", 9.8, SOFT, { italic: true }),
    price: st("lora", 11.5, INK),
    codes: st("lora", 7, MUTED),
    tags: st("lora", 7, GOLD, { italic: true }),
    itemNote: st("lora", 8, BORDEAUX, { italic: true }),
    textTitle: st("playfair", 13, BORDEAUX, { italic: true }),
    text: st("lora", 11, INK),
    textLine: st("lora", 11, INK, { bold: true }),
    course: st("playfair", 12.5, BORDEAUX, { italic: true }),
    orLine: st("lora", 9.8, SOFT, { italic: true }),
    part: st("playfair", 22, BORDEAUX, { italic: true }),
    sizeHead: st("lora", 7, GOLD, { caps: "upper", tracking: 10 }),
    menuNote: st("lora", 10, SOFT, { italic: true }),
    legend: st("lora", 8, MUTED),
  },
  headAlign: "center",
  rule: true,
  sectionSpace: 20,
  dishSpace: 8.5,
  lineSpacing: "normal",
  orText: OR_TEXT.fallback,
  setLayout: "indent",
  choiceText: CHOICE_TEXT.fallback,
  preset: "elegant",
  page: { format: "a4", marginX: 22, marginY: 20 },
  columns: { number: 22, price: 44 },
  variantIndent: 0,
  headWidth: 100,
  euro: false,
  web: true,
  logoPlace: "center",
  logoOnly: false,
  logoHidden: false,
};

const OLIVE = "#5b6b2f";
const BISTRO: Design = {
  styles: {
    house: st("josefin", 26, "#000000", { bold: true, caps: "upper", tracking: 12 }),
    title: st("josefin", 9, OLIVE, { caps: "upper", tracking: 22 }),
    lead: st("baskerville", 10, MUTED, { italic: true }),
    section: st("josefin", 13, OLIVE, { bold: true, caps: "upper", tracking: 12 }),
    subsection: st("josefin", 10.5, OLIVE, { bold: true, caps: "upper", tracking: 12 }),
    sectionNote: st("baskerville", 9, MUTED, { italic: true }),
    number: st("josefin", 10.5, OLIVE, { bold: true }),
    dish: st("baskerville", 11, "#000000", { bold: true }),
    description: st("baskerville", 9.6, SOFT, { italic: true }),
    variant: st("baskerville", 9.6, SOFT, { italic: true }),
    price: st("josefin", 12, "#000000", { bold: true }),
    codes: st("josefin", 7, MUTED),
    tags: st("josefin", 7, OLIVE, { caps: "upper", tracking: 5 }),
    itemNote: st("josefin", 8, OLIVE),
    textTitle: st("josefin", 12, OLIVE, { bold: true, caps: "upper", tracking: 8 }),
    text: st("baskerville", 10.5, "#000000"),
    textLine: st("baskerville", 10.5, "#000000", { bold: true }),
    course: st("josefin", 10.5, OLIVE, { bold: true, caps: "upper", tracking: 12 }),
    orLine: st("baskerville", 9.6, SOFT, { italic: true }),
    part: st("josefin", 18, OLIVE, { bold: true, caps: "upper", tracking: 12 }),
    sizeHead: st("josefin", 7, MUTED, { caps: "upper", tracking: 10 }),
    menuNote: st("baskerville", 10, SOFT, { italic: true }),
    legend: st("josefin", 8, MUTED),
  },
  headAlign: "center",
  rule: true,
  sectionSpace: 16,
  dishSpace: 7.5,
  lineSpacing: "normal",
  orText: OR_TEXT.fallback,
  setLayout: "indent",
  choiceText: CHOICE_TEXT.fallback,
  preset: "bistro",
  page: { format: "a4", marginX: 16, marginY: 16 },
  columns: { number: 22, price: 40 },
  variantIndent: 0,
  headWidth: 100,
  euro: false,
  web: true,
  logoPlace: "center",
  logoOnly: false,
  logoHidden: false,
};

export const PRESETS = [
  { id: "classic", label: "Klassisch", design: CLASSIC },
  { id: "modern", label: "Modern", design: MODERN },
  { id: "elegant", label: "Elegant", design: ELEGANT },
  { id: "bistro", label: "Bistro", design: BISTRO },
] as const;
export type PresetId = (typeof PRESETS)[number]["id"];

/** A fresh copy of a template; „Klassisch“ when none is named. */
export const presetDesign = (id: PresetId = "classic"): Design => structuredClone((PRESETS.find((p) => p.id === id) ?? PRESETS[0]).design);
export const DEFAULT_DESIGN: Design = presetDesign();

const HEX = /^#[0-9a-f]{6}$/;
const num = (v: unknown, min: number, max: number, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v * 10) / 10)) : fallback);

function styleOf(v: unknown, fallback: TextStyle): TextStyle {
  const o = v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  return {
    font: DESIGN_FONTS.some((f) => f.id === o.font) ? (o.font as FontId) : fallback.font,
    size: num(o.size, SIZE_LIMITS.min, SIZE_LIMITS.max, fallback.size),
    color: typeof o.color === "string" && HEX.test(o.color.toLowerCase()) ? o.color.toLowerCase() : fallback.color,
    bold: typeof o.bold === "boolean" ? o.bold : fallback.bold,
    italic: typeof o.italic === "boolean" ? o.italic : fallback.italic,
    caps: o.caps === "upper" || o.caps === "small" || o.caps === "normal" ? o.caps : fallback.caps,
    tracking: num(o.tracking, 0, 40, fallback.tracking),
  };
}

/**
 * The parts' styles. A style from before the further prices and the one-line texts has no style for
 * them: they look as they did, the further prices like the description, a text of one line like the
 * text in bold.
 */
function stylesOf(styles: Record<string, unknown>): Record<StyleKey, TextStyle> {
  const own = (k: StyleKey) => styleOf(styles[k], CLASSIC.styles[k]);
  const fallback = (k: StyleKey): TextStyle =>
    k === "variant"
      ? own("description")
      : k === "textLine"
        ? { ...own("text"), bold: true }
        : k === "part"
          ? { ...own("section"), size: Math.min(SIZE_LIMITS.max, Math.round(own("section").size * 14) / 10) }
          : // A style from before S32: a course's title as a small section's, „oder“ as the description in italics.
            k === "course"
            ? { ...own("section"), size: Math.max(SIZE_LIMITS.min, Math.round((own("section").size * 130) / 16) / 10) }
            : k === "orLine"
              ? { ...own("description"), italic: true }
              : CLASSIC.styles[k];
  // In the order of STYLE_KEYS: the editor compares styles as text.
  const out = Object.fromEntries(STYLE_KEYS.map((k) => [k, fitDisplay(k, styleOf(styles[k], fallback(k)), fallback(k).font)])) as Record<StyleKey, TextStyle>;
  const course = newerCourse(out.course);
  // A style from before S44: the subsection as its own course of a set menu, not as Klassisch's.
  return { ...out, course, subsection: styles.subsection === undefined ? course : out.subsection };
}

/**
 * A course's title still exactly as a template had it before the look of set menus (S32 § 12) was
 * nobody's choice: it gets the template's new size (Martin, 27.10.2026: „Weg 2“). Smaller than the
 * dish's name, it was quieter than what it heads. A size chosen in the editor stays.
 */
const OLD_COURSES: { font: FontId; size: number; color: string; bold: boolean; italic: boolean; caps: Caps; tracking: number; now: number }[] = [
  { font: "times", size: 10.5, color: ACCENT, bold: false, italic: false, caps: "small", tracking: 4, now: 13 },
  { font: "garamond", size: 11.2, color: ACCENT, bold: false, italic: false, caps: "small", tracking: 4, now: 13 },
  { font: "montserrat", size: 8.5, color: NAVY, bold: true, italic: false, caps: "upper", tracking: 12, now: 10 },
  { font: "playfair", size: 11, color: BORDEAUX, bold: false, italic: true, caps: "normal", tracking: 0, now: 12.5 },
  { font: "josefin", size: 9, color: OLIVE, bold: true, italic: false, caps: "upper", tracking: 12, now: 10.5 },
];
function newerCourse(s: TextStyle): TextStyle {
  const old = OLD_COURSES.find((o) => o.font === s.font && o.size === s.size && o.color.toLowerCase() === s.color.toLowerCase() && o.bold === s.bold && o.italic === s.italic && o.caps === s.caps && o.tracking === s.tracking);
  return old ? { ...s, size: old.now } : s;
}

/** The word for „oder“ (S32): one line, at most OR_TEXT.max letters; empty is „oder“. */
export const orTextOf = (v: unknown): string => (typeof v === "string" ? v.normalize("NFC").replace(/\s+/g, " ").trim().slice(0, OR_TEXT.max) : "") || OR_TEXT.fallback;
/** The word for „zur Wahl“ (S32 § 12), as the one for „oder“. */
export const choiceTextOf = (v: unknown): string => (typeof v === "string" ? v.normalize("NFC").replace(/\s+/g, " ").trim().slice(0, CHOICE_TEXT.max) : "") || CHOICE_TEXT.fallback;

/** A style from what is stored or sent; whatever is missing or wrong comes from „Klassisch“. */
export function designOf(v: unknown): Design {
  const o = v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const styles = (o.styles && typeof o.styles === "object" ? o.styles : {}) as Record<string, unknown>;
  const page = (o.page && typeof o.page === "object" ? o.page : {}) as Record<string, unknown>;
  const columns = (o.columns && typeof o.columns === "object" ? o.columns : {}) as Record<string, unknown>;
  const ownStyles = stylesOf(styles);
  const drinks = o.drinkStyles && typeof o.drinkStyles === "object" && !Array.isArray(o.drinkStyles) ? (o.drinkStyles as Record<string, unknown>) : null;
  const drinkStyles = drinks ? (Object.fromEntries(DRINK_STYLE_KEYS.map((k) => [k, fitDisplay(k, styleOf(drinks[k], ownStyles[k]), ownStyles[k].font)])) as Record<DrinkStyleKey, TextStyle>) : null;
  const setDrinks = o.setDrinkStyles && typeof o.setDrinkStyles === "object" && !Array.isArray(o.setDrinkStyles) ? (o.setDrinkStyles as Record<string, unknown>) : null;
  const logoSize = Math.round(num(o.logoSize, LOGO_SIZE.min, LOGO_SIZE.max, LOGO_SIZE.fallback));
  const footLogo = FOOT_LOGO_PLACES.find((f) => f === o.footLogo);
  const footLogoSize = Math.round(num(o.footLogoSize, FOOT_LOGO_SIZE.min, FOOT_LOGO_SIZE.max, FOOT_LOGO_SIZE.fallback));
  const translation = translationRuleOf(o.translation);
  return {
    styles: ownStyles,
    ...(drinkStyles ? { drinkStyles } : {}),
    ...(drinks && typeof o.drinkEuro === "boolean" ? { drinkEuro: o.drinkEuro } : {}),
    // Whatever is missing comes from the drinks' look, as coupling would give it.
    ...(setDrinks ? { setDrinkStyles: Object.fromEntries(SET_DRINK_STYLE_KEYS.map((k) => [k, fitDisplay(k, styleOf(setDrinks[k], (drinkStyles ?? ownStyles)[k]), (drinkStyles ?? ownStyles)[k].font)])) as Record<SetDrinkStyleKey, TextStyle> } : {}),
    headAlign: o.headAlign === "left" ? "left" : "center",
    rule: typeof o.rule === "boolean" ? o.rule : CLASSIC.rule,
    sectionSpace: num(o.sectionSpace, SECTION_SPACE.min, SECTION_SPACE.max, CLASSIC.sectionSpace),
    dishSpace: num(o.dishSpace, DISH_SPACE.min, DISH_SPACE.max, CLASSIC.dishSpace),
    lineSpacing: LINE_SPACINGS.some((l) => l.id === o.lineSpacing) ? (o.lineSpacing as LineSpacing) : "normal",
    orText: orTextOf(o.orText),
    setLayout: SET_LAYOUTS.some((l) => l.id === o.setLayout) ? (o.setLayout as SetLayout) : "indent",
    choiceText: choiceTextOf(o.choiceText),
    preset: PRESETS.some((p) => p.id === o.preset) ? (o.preset as PresetId) : o.preset === undefined ? CLASSIC.preset : "",
    page: {
      format: PAGE_FORMATS.some((f) => f.id === page.format) ? (page.format as PageFormat) : CLASSIC.page.format,
      marginX: num(page.marginX, MARGIN_X.min, MARGIN_X.max, CLASSIC.page.marginX),
      marginY: num(page.marginY, MARGIN_Y.min, MARGIN_Y.max, CLASSIC.page.marginY),
    },
    columns: { number: num(columns.number, NUMBER_COLUMN.min, NUMBER_COLUMN.max, CLASSIC.columns.number), price: num(columns.price, PRICE_COLUMN.min, PRICE_COLUMN.max, CLASSIC.columns.price) },
    variantIndent: num(o.variantIndent, VARIANT_INDENT.min, VARIANT_INDENT.max, CLASSIC.variantIndent),
    headWidth: num(o.headWidth, HEAD_WIDTH.min, HEAD_WIDTH.max, CLASSIC.headWidth),
    euro: typeof o.euro === "boolean" ? o.euro : CLASSIC.euro,
    web: typeof o.web === "boolean" ? o.web : CLASSIC.web,
    logoPlace: logoPlaceOf(o.logoPlace),
    logoOnly: o.logoOnly === true,
    logoHidden: o.logoHidden === true,
    ...(logoSize === LOGO_SIZE.fallback ? {} : { logoSize }),
    ...(footLogo ? { footLogo, ...(footLogoSize === FOOT_LOGO_SIZE.fallback ? {} : { footLogoSize }) } : {}),
    ...(o.align === "center" ? { align: "center" as const } : {}),
    ...(COURSE_MARKS.some((m) => m.id === o.courseMark) ? { courseMark: o.courseMark as CourseMark } : {}),
    ...(translation ? { translation } : {}),
  };
}

/** Points above a subsection's title (S44): more than between two dishes, less than above a section. */
export const subsectionSpace = (d: Pick<Design, "dishSpace" | "sectionSpace">) => Math.round(((d.dishSpace + Math.max(d.dishSpace, d.sectionSpace)) / 2) * 10) / 10;

/** Whether the drinks' prices carry „€“: their own choice with their own look, else the food's. */
export const drinkEuroOf = (d: Design): boolean => (d.drinkStyles ? (d.drinkEuro ?? d.euro) : d.euro);

/** The style as the drinks are drawn (S22): their own look of a dish's parts and their „€“ when they have one. */
export const drinkDesign = (d: Design): Design => (d.drinkStyles ? { ...d, styles: { ...d.styles, ...d.drinkStyles }, euro: drinkEuroOf(d) } : d);

/** Whether the drinks' own look is the food's after all (then coupling again asks nothing). */
export const drinkStylesSame = (d: Design) => !d.drinkStyles || (drinkEuroOf(d) === d.euro && DRINK_STYLE_KEYS.every((k) => JSON.stringify(d.drinkStyles![k]) === JSON.stringify(d.styles[k])));

/**
 * The style as a drink in a set menu is drawn (S53): its own look there when it has one, else the
 * drinks' (which without theirs is the food's): food → drinks → drinks in a set menu. Its „€“ is the drinks'.
 */
export const setDrinkDesign = (d: Design): Design => {
  const drinks = drinkDesign(d);
  return d.setDrinkStyles ? { ...drinks, styles: { ...drinks.styles, ...d.setDrinkStyles } } : drinks;
};

/** Whether the look of drinks in a set menu is the drinks' after all (then coupling again asks nothing). */
export const setDrinkStylesSame = (d: Design) => !d.setDrinkStyles || SET_DRINK_STYLE_KEYS.every((k) => JSON.stringify(d.setDrinkStyles![k]) === JSON.stringify(drinkDesign(d).styles[k]));

/** A style with its name; a business keeps up to MAX_DESIGNS. */
export interface NamedDesign {
  id: string;
  name: string;
  design: Design;
}
export interface Designs {
  list: NamedDesign[];
  /** The house style: for every menu that has none of its own. */
  main: string;
}
export const MAX_DESIGNS = 12;
export const HOUSE_STYLE = "Hausstil";

/** One style, „Klassisch“, as the house style. */
export const defaultDesigns = (): Designs => ({ list: [{ id: "haus", name: HOUSE_STYLE, design: presetDesign() }], main: "haus" });

/** The styles from what is stored or sent: at least one, names unique, the house style among them. */
export function designsOf(v: unknown): Designs {
  const o = v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const seen = new Set<string>();
  const names = new Set<string>();
  const list: NamedDesign[] = [];
  for (const raw of Array.isArray(o.list) ? o.list.slice(0, MAX_DESIGNS) : []) {
    const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const id = typeof r.id === "string" ? r.id.replace(/[^\w-]/g, "").slice(0, 40) : "";
    let name = typeof r.name === "string" ? r.name.replace(/\s+/g, " ").trim().slice(0, 40) : "";
    if (!id || seen.has(id)) continue;
    if (!name) name = `Stil ${list.length + 1}`;
    while (names.has(name.toLowerCase())) name = `${name} (2)`;
    seen.add(id);
    names.add(name.toLowerCase());
    list.push({ id, name, design: designOf(r.design) });
  }
  if (!list.length) return defaultDesigns();
  const main = typeof o.main === "string" && seen.has(o.main) ? o.main : list[0]!.id;
  return { list, main };
}

/** The style of a menu with its name and id: its own, or the house style. */
export function namedDesignFor(designs: Designs | null, style: string): NamedDesign {
  const d = designs ?? defaultDesigns();
  return d.list.find((x) => x.id === style) ?? d.list.find((x) => x.id === d.main) ?? d.list[0]!;
}

/** The style of a menu: its own, or the house style. */
export const designFor = (designs: Designs | null, style: string): Design => namedDesignFor(designs, style).design;

/** The free fonts a style uses, as files, for the PDF and the printed page. */
export function designFiles(d: Design): string[] {
  const files = new Set<string>();
  const translated = [d.styles.description, d.styles.text, d.drinkStyles?.description, d.setDrinkStyles?.description].filter((s) => s !== undefined).map((s) => translationStyle(s, d));
  for (const s of [...STYLE_KEYS.map((k) => d.styles[k]), ...Object.values(d.drinkStyles ?? {}), ...Object.values(d.setDrinkStyles ?? {}), ...translated]) {
    const file = smallCapsFont(s)?.file ?? fontFile(s.font, s.bold, s.italic);
    if (file) files.add(file);
  }
  return [...files];
}

/**
 * A unit after a figure („0,3 l“, „30 cm“, „250 g“): its sign is fixed, so „GROSS“ and small capitals
 * leave it as it is (S46): never „30 CM“.
 */
const UNIT = /(?<=\d[\s\u00a0\u202f]?)(?:mm|cm|ml|cl|dl|mg|kg|g|l)(?![\p{L}\d])/gu;

/** A text in parts, each saying whether it is such a unit (S46). */
export function unitParts(text: string): { text: string; unit: boolean }[] {
  const out: { text: string; unit: boolean }[] = [];
  let at = 0;
  for (const m of text.matchAll(UNIT)) {
    if (m.index > at) out.push({ text: text.slice(at, m.index), unit: false });
    out.push({ text: m[0], unit: true });
    at = m.index + m[0].length;
  }
  if (at < text.length || !out.length) out.push({ text: text.slice(at), unit: false });
  return out;
}

/** The language of a text on a card: German or a further one (S36). */
export type TextLang = "de" | Lang;
/** Between the German and its translation in a short text of a bilingual card: „Vorspeisen / Starters“ (S36). */
export const BOTH = " / ";

/**
 * A text in its languages (docs/21-tuerkisch.md § 3.3): one part in `lang`; on a bilingual card
 * (`second`, the further language) a short text as `viewIn` joins it, „Pide çeşitleri“ after the last
 * „ / “, in the second. The separator stays with the first part.
 */
export function langParts(text: string, lang: TextLang = "de", second?: Lang): { text: string; lang: TextLang }[] {
  const at = second && second !== lang ? text.lastIndexOf(BOTH) : -1;
  return at < 0 ? [{ text, lang }] : [{ text: text.slice(0, at + BOTH.length), lang }, { text: text.slice(at + BOTH.length), lang: second! }];
}

/** Capitals by the rules of the language: Turkish i is İ, ı is I; „Vorspeisen“ never „VORSPEİSEN“. */
export const upperIn = (text: string, lang: TextLang = "de") => text.toLocaleUpperCase(lang === "de" ? "de-DE" : lang);

/**
 * Text as its style writes it: in capitals for „GROSS“, by the language of each part, units as they
 * are (S46); small capitals are drawn by each output.
 */
export const casedText = (text: string, s: TextStyle, lang: TextLang = "de", second?: Lang) =>
  s.caps === "upper" ? langParts(text, lang, second).map((part) => unitParts(part.text).map((p) => (p.unit ? p.text : upperIn(p.text, part.lang))).join("")).join("") : text;

/** Whether a text in capitals differs by its language from German capitals: then each output writes them itself. */
export const capsDiffer = (text: string, lang: TextLang) => lang !== "de" && upperIn(text, lang) !== upperIn(text);

/** Figure and unit kept together by a space that does not break (S46): „30cm“ and „30 cm“ as „30 cm“. */
export const unitSpaced = (text: string) => text.replace(/(\d)[ \u202f]?(?=(?:mm|cm|ml|cl|dl|mg|kg|g|l)(?![\p{L}\d]))/gu, "$1\u00a0");
